import { Kaart, KaartKop, Melding, PaginaKop } from '../components/ui';
import { Symbool } from '../components/icons';
import { useAuth } from '../lib/auth';
import { TIERS, niveauVan } from '../lib/prestaties';
import { useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';

/* ==========================================================================
   Titels — het rek waar je uithangbord vandaan komt.

   Nog niet af, en dat staat er ook op. Wat hier al staat is de vorm: wat een
   titel is, waar hij vandaan komt en hoe je er een kiest. De voorwaarden en de
   lijst zelf werken we later uit; dit scherm bestaat zodat we ergens naartoe
   kunnen werken en niet later alsnog een plek moeten zoeken.

   Bewust nog niets in de database: zodra de regels staan hoort een gekozen
   titel bij je profiel, maar tot die tijd zou een kolom alleen maar leeg staan.
   ========================================================================== */

interface Titel {
  sleutel: string;
  naam: string;
  /** Waar hij vandaan komt, in gewone taal. */
  voorwaarde: string;
  /** Welke kant hij op hoort: spelen, winnen, volhouden of toeval. */
  soort: 'spel' | 'reeks' | 'trouw' | 'gek';
  ikoon: string;
}

/*
 * Een eerste greep, om te laten zien wat voor soort namen we bedoelen. Deze
 * lijst is nog niet vastgesteld — hij staat hier als aanzet, niet als belofte.
 */
const AANZET: Titel[] = [
  { sleutel: 'nieuweling', naam: 'Nieuweling', voorwaarde: 'Je eerste potje gespeeld.', soort: 'spel', ikoon: 'bal' },
  { sleutel: 'stofzuiger', naam: 'De Stofzuiger', voorwaarde: 'Drie keer winnen zonder tegendoelpunt.', soort: 'spel', ikoon: 'bezem' },
  { sleutel: 'muur', naam: 'De Muur', voorwaarde: 'Tien potjes op rij met minder dan vijf tegen.', soort: 'spel', ikoon: 'schild' },
  { sleutel: 'onaantastbaar', naam: 'Onaantastbaar', voorwaarde: 'Tien overwinningen op rij.', soort: 'reeks', ikoon: 'vlam' },
  { sleutel: 'klimmer', naam: 'De Klimmer', voorwaarde: 'Twee rangen stijgen in één kwartaal.', soort: 'reeks', ikoon: 'berg' },
  { sleutel: 'stamgast', naam: 'Stamgast', voorwaarde: 'Twaalf weken achter elkaar gespeeld.', soort: 'trouw', ikoon: 'kalender' },
  { sleutel: 'vrijdagheld', naam: 'Vrijdagheld', voorwaarde: 'Vijfentwintig potjes op een vrijdagmiddag.', soort: 'trouw', ikoon: 'glas' },
  { sleutel: 'pechvogel', naam: 'Pechvogel', voorwaarde: 'Vijf keer verliezen met één doelpunt verschil.', soort: 'gek', ikoon: 'hart' },
  { sleutel: 'nachtbraker', naam: 'Nachtbraker', voorwaarde: 'Een potje na achten gespeeld.', soort: 'gek', ikoon: 'klok' }
];

const SOORT_LABEL: Record<Titel['soort'], string> = {
  spel: 'Aan de tafel',
  reeks: 'Op dreef',
  trouw: 'Volhouden',
  gek: 'Voor de sier'
};

export default function Titels() {
  const { pouleId } = usePoule();
  const { profile } = useAuth();
  const { matches, elo } = useLeague(pouleId);

  const niveau = profile ? niveauVan(profile.id, matches, elo.verloop[profile.id] ?? []) : null;

  const groepen = (['spel', 'reeks', 'trouw', 'gek'] as const).map((soort) => ({
    soort,
    titels: AANZET.filter((t) => t.soort === soort)
  }));

  return (
    <>
      <PaginaKop
        titel="Titels"
        onder="Een titel staat straks achter je naam in de ranglijst. Je verdient ze met wat je aan de tafel doet, en je kiest zelf welke je draagt."
      />

      <Melding>
        Dit scherm is nog niet af. De lijst hieronder is een aanzet: de namen en de voorwaarden staan nog niet vast, en
        kiezen kan pas als ze in de database staan. Wat er al wél werkt is je niveau — dat volgt uit je potjes en je
        prestaties, en zie je op je profiel.
      </Melding>

      {niveau ? (
        <div className="mt-3.5">
          <Kaart>
            <KaartKop titel="Waar je nu staat" hint="niveau bepaalt straks welke titels je mag dragen" />
            <div className="flex flex-wrap items-center gap-5">
              <span className="flex items-center gap-3">
                <span className="kop nums flex h-14 w-14 items-center justify-center rounded-full border-2 border-krijt-900 text-[20px]">
                  {niveau.level}
                </span>
                <span>
                  <span className="opschrift block text-krijt-500">Niveau</span>
                  <span className="nums text-sm text-krijt-600">
                    {niveau.inLevel} / {niveau.levelKost} naar het volgende
                  </span>
                </span>
              </span>

              <span className="min-w-48 flex-1">
                <span className="block h-2 overflow-hidden rounded-full bg-krijt-200">
                  <span
                    className="block h-full bg-veld-700 transition-all duration-500"
                    style={{ width: `${Math.round(niveau.deel * 100)}%` }}
                  />
                </span>
                <span className="nums mt-1.5 block text-xs text-krijt-500">{niveau.xp} punten in totaal</span>
              </span>
            </div>
          </Kaart>
        </div>
      ) : null}

      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
        {groepen.map((g) => (
          <Kaart key={g.soort}>
            <KaartKop titel={SOORT_LABEL[g.soort]} hint={`${g.titels.length} titels`} />
            <div className="flex flex-col">
              {g.titels.map((t) => (
                <div
                  key={t.sleutel}
                  className="flex items-start gap-3 border-b border-krijt-200 py-2.5 last:border-b-0 opacity-60"
                >
                  <Symbool naam={t.ikoon} className="mt-0.5 text-krijt-400" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{t.naam}</span>
                    <span className="block text-xs text-krijt-600">{t.voorwaarde}</span>
                  </span>
                  <span className="ml-auto shrink-0 text-[11px] font-semibold uppercase tracking-wide text-krijt-400">
                    nog niet te verdienen
                  </span>
                </div>
              ))}
            </div>
          </Kaart>
        ))}
      </div>

      <div className="mt-3.5">
        <Kaart>
          <KaartKop titel="Hoe dit gaat werken" />
          <ol className="ml-4 list-decimal space-y-2 text-sm leading-relaxed text-krijt-600">
            <li>
              Elke titel hangt aan een voorwaarde die uit de potjes te rekenen is, net als de prestaties. Geen handwerk,
              geen beheerder die ze uitdeelt.
            </li>
            <li>
              Wat je verdiend hebt blijft van jou. Een titel zakt niet weg als je een tijdje slecht speelt — anders
              wordt het een tweede ranglijst, en die hebben we al.
            </li>
            <li>
              Je draagt er één tegelijk, gekozen op je profiel. De rest blijft in dit rek staan, zichtbaar voor
              iedereen die je profiel bekijkt.
            </li>
            <li>
              De zeven tiers van de prestaties ({TIERS.map((t) => t.label).join(', ')}) blijven waar ze zijn; een titel
              is iets anders dan een trede.
            </li>
          </ol>
        </Kaart>
      </div>
    </>
  );
}
